// src/pages/motolivraison/reversements.page.jsx
//
// Liste des reversements commerçants avec actions admin :
// - Créer un nouveau reversement (avec preview des commandes non reversées)
// - Marquer comme payé (avec référence externe)
// - Annuler

import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Modal, message, Card, Row, Col, Tag, Space, Breadcrumb,
  Select, Form, Input, Button, Descriptions, InputNumber,
} from 'antd';
import {
  PlusOutlined, DollarOutlined, ClockCircleOutlined, CheckCircleOutlined,
  CloseCircleOutlined, RightOutlined, EyeOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { motolivraisonService } from '../../common/services';
import {
  LOCALE_TABLE, PAGINATION_TABLE, BTN_ACTION, COULEURS_ACTION, cmpTexte, CarteStat,
} from './table.helpers';
import './css/motolivraison-table.css';

const STATUS_CONFIG = {
  pending: { color: 'orange', label: 'En attente' },
  processing: { color: 'blue', label: 'En cours' },
  paid: { color: 'green', label: 'Payé' },
  failed: { color: 'red', label: 'Échec' },
  cancelled: { color: 'default', label: 'Annulé' },
};

const Reversements = () => {
  const [reversements, setReversements] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [createForm] = Form.useForm();
  const [payForm] = Form.useForm();

  // Données pour le create
  const [commerces, setCommerces] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const stats = {
    total: reversements.length,
    pending: reversements.filter(r => r.status === 'pending').length,
    paid: reversements.filter(r => r.status === 'paid').length,
    montantPaid: reversements.filter(r => r.status === 'paid').reduce((s, r) => s + (r.montantNet || 0), 0),
  };

  const fetchReversements = useCallback(async () => {
    setLoading(true);
    try {
      // Tri et filtres de colonnes portent sur l'ensemble des reversements
      const data = await motolivraisonService.listReversements({ limit: 1000 });
      setReversements(data?.reversements || []);
    } catch (e) {
      message.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReversements();
  }, [fetchReversements]);

  const openCreate = async () => {
    setCreateModalOpen(true);
    createForm.resetFields();
    setPreviewData(null);
    try {
      const data = await motolivraisonService.listCommerces({ status: 'approved' });
      setCommerces(data?.commerces || []);
    } catch (e) {
      message.error('Erreur lors du chargement des commerces');
    }
  };

  const handlePreviewCommandes = async () => {
    try {
      const values = await createForm.validateFields(['commerceId']);
      setPreviewLoading(true);
      const data = await motolivraisonService.previewCommandesNonReversees(values.commerceId);
      setPreviewData(data);
    } catch (e) {
      if (e?.errorFields) return;
      message.error('Erreur lors de la prévisualisation');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      await motolivraisonService.createReversement({
        commerceId: values.commerceId,
        frequence: values.frequence || 'custom',
        frais: values.frais || 0,
        methodePaiement: values.methodePaiement || 'wave',
        telephoneWavePaiement: values.telephoneWavePaiement,
        notes: values.notes,
      });
      message.success('Reversement créé');
      setCreateModalOpen(false);
      fetchReversements();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur');
    }
  };

  const openPay = (record) => {
    setSelected(record);
    payForm.resetFields();
    setPayModalOpen(true);
  };

  const handlePay = async () => {
    try {
      const { referenceExterne } = await payForm.validateFields();
      await motolivraisonService.payReversement(selected._id, referenceExterne);
      message.success('Reversement marqué comme payé. Les commandes liées sont passées en "soldée".');
      setPayModalOpen(false);
      fetchReversements();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur');
    }
  };

  const handleCancel = (record) => {
    Modal.confirm({
      title: 'Annuler ce reversement ?',
      content: 'Les commandes liées seront déliées et redeviendront disponibles pour un nouveau reversement.',
      okText: 'Confirmer',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await motolivraisonService.cancelReversement(record._id);
          message.success('Reversement annulé');
          fetchReversements();
        } catch (e) {
          message.error(e?.message || 'Erreur');
        }
      },
    });
  };

  const openDetail = (record) => {
    setSelected(record);
    setDetailModalOpen(true);
  };

  const columns = [
    {
      title: 'Commerce', key: 'commerce', width: 200, ellipsis: true,
      sorter: (a, b) => cmpTexte(a.commerceId?.nom, b.commerceId?.nom),
      render: (_, r) => r.commerceId?.nom || '—',
    },
    {
      title: 'Du', dataIndex: 'periodeDebut', key: 'debut', width: 120,
      sorter: (a, b) => new Date(a.periodeDebut || 0) - new Date(b.periodeDebut || 0),
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Au', dataIndex: 'periodeFin', key: 'fin', width: 120,
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.periodeFin || 0) - new Date(b.periodeFin || 0),
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Commandes', dataIndex: 'nombreCommandes', key: 'cmd', width: 120, align: 'center',
      sorter: (a, b) => (a.nombreCommandes || 0) - (b.nombreCommandes || 0),
    },
    {
      title: 'Brut', dataIndex: 'montantBrut', key: 'brut', width: 120, align: 'right',
      sorter: (a, b) => (a.montantBrut || 0) - (b.montantBrut || 0),
      render: (v) => `${Number(v || 0).toLocaleString('fr-FR')} F`,
    },
    {
      title: 'Commission', dataIndex: 'commissionTotale', key: 'commission', width: 130, align: 'right',
      sorter: (a, b) => (a.commissionTotale || 0) - (b.commissionTotale || 0),
      render: (v) => <span style={{ color: '#722ed1' }}>{Number(v || 0).toLocaleString('fr-FR')} F</span>,
    },
    {
      title: 'Net à payer', dataIndex: 'montantNet', key: 'net', width: 130, align: 'right',
      sorter: (a, b) => (a.montantNet || 0) - (b.montantNet || 0),
      render: (v) => <strong style={{ color: '#52c41a' }}>{Number(v || 0).toLocaleString('fr-FR')} F</strong>,
    },
    {
      title: 'Statut', dataIndex: 'status', key: 'status', width: 130,
      filters: Object.entries(STATUS_CONFIG).map(([value, { label }]) => ({ text: label, value })),
      onFilter: (value, r) => r.status === value,
      render: (s) => {
        const cfg = STATUS_CONFIG[s] || { color: 'default', label: s };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Actions', key: 'actions', width: 330, align: 'center', fixed: 'right',
      render: (_, r) => (
        <Space size={6}>
          <Button size="small" icon={<EyeOutlined />} style={BTN_ACTION(COULEURS_ACTION.voir)}
            onClick={() => openDetail(r)}>
            Détails
          </Button>
          {r.status === 'pending' && (
            <>
              <Button size="small" icon={<CheckCircleOutlined />} style={BTN_ACTION(COULEURS_ACTION.valider)}
                onClick={() => openPay(r)}>
                Payer
              </Button>
              <Button size="small" icon={<CloseCircleOutlined />} style={BTN_ACTION(COULEURS_ACTION.danger)}
                onClick={() => handleCancel(r)}>
                Annuler
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item>Reversements</Breadcrumb.Item>
        </Breadcrumb>

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}><CarteStat label="Total" value={stats.total} icon={<DollarOutlined />} /></Col>
          <Col xs={12} sm={6}><CarteStat label="En attente" value={stats.pending} icon={<ClockCircleOutlined />} color="#fa8c16" /></Col>
          <Col xs={12} sm={6}><CarteStat label="Payés" value={stats.paid} icon={<CheckCircleOutlined />} color="#52c41a" /></Col>
          <Col xs={12} sm={6}><CarteStat label="Montant total payé" value={`${Number(stats.montantPaid || 0).toLocaleString('fr-FR')} F`} icon={<DollarOutlined />} color="#52c41a" /></Col>
        </Row>

        <Card
          title="Reversements commerçants"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Nouveau reversement
            </Button>
          }
        >
          <Table
            className="moto-table"
            columns={columns}
            dataSource={reversements.map(r => ({ ...r, key: r._id }))}
            loading={loading}
            size="middle"
            locale={LOCALE_TABLE}
            pagination={PAGINATION_TABLE('reversement')}
            scroll={{ x: 1610 }}
          />
        </Card>
      </div>

      {/* Modal Create */}
      <Modal
        title="Créer un reversement"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={handleCreate}
        okText="Créer le reversement"
        cancelText="Annuler"
        width={700}
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="commerceId"
            label="Commerce"
            rules={[{ required: true, message: 'Veuillez sélectionner un commerce' }]}
          >
            <Select
              placeholder="Sélectionner un commerce"
              showSearch
              optionFilterProp="label"
              options={commerces.map(c => ({ value: c._id, label: c.nom }))}
              onChange={() => setPreviewData(null)}
            />
          </Form.Item>

          <Button type="dashed" onClick={handlePreviewCommandes} loading={previewLoading} style={{ marginBottom: 16 }} block>
            Prévisualiser les commandes à reverser
          </Button>

          {previewData && (
            <Card size="small" style={{ marginBottom: 16, background: '#f6ffed', border: '1px solid #b7eb8f' }}>
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="Commandes">{previewData.nombreCommandes}</Descriptions.Item>
                <Descriptions.Item label="Montant brut"><strong>{previewData.montantBrut} F</strong></Descriptions.Item>
                <Descriptions.Item label="Commission TIGOON" span={2}>{previewData.commissionTotale} F</Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="frequence" label="Fréquence" initialValue="custom">
                <Select options={[
                  { value: 'daily', label: 'Quotidien' },
                  { value: 'weekly', label: 'Hebdomadaire' },
                  { value: 'monthly', label: 'Mensuel' },
                  { value: 'custom', label: 'Personnalisé' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="methodePaiement" label="Méthode" initialValue="wave">
                <Select options={[
                  { value: 'wave', label: 'Wave' },
                  { value: 'wave_business', label: 'Wave Business' },
                  { value: 'virement_bancaire', label: 'Virement bancaire' },
                  { value: 'especes', label: 'Espèces' },
                  { value: 'autre', label: 'Autre' },
                ]} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="telephoneWavePaiement" label="Téléphone Wave (paiement)">
                <Input placeholder="+225..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="frais" label="Frais éventuels (FCFA)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Pay */}
      <Modal
        title="Marquer le reversement comme payé"
        open={payModalOpen}
        onCancel={() => setPayModalOpen(false)}
        onOk={handlePay}
        okText="Confirmer le paiement"
        cancelText="Annuler"
      >
        {selected && (
          <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
            <p><strong>Commerce :</strong> {selected.commerceId?.nom}</p>
            <p><strong>Montant net :</strong> <strong style={{ color: '#52c41a', fontSize: 18 }}>{selected.montantNet} F</strong></p>
          </Card>
        )}
        <Form form={payForm} layout="vertical">
          <Form.Item
            name="referenceExterne"
            label="Référence externe (Wave Payout, virement...)"
            rules={[{ required: true, message: 'Référence requise' }]}
          >
            <Input placeholder="ex: WAVE-PAYOUT-12345" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Detail */}
      <Modal
        title="Détail du reversement"
        open={detailModalOpen}
        onCancel={() => setDetailModalOpen(false)}
        footer={null}
        width={700}
      >
        {selected && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Commerce" span={2}>{selected.commerceId?.nom}</Descriptions.Item>
            <Descriptions.Item label="Période début">{dayjs(selected.periodeDebut).format('DD/MM/YYYY')}</Descriptions.Item>
            <Descriptions.Item label="Période fin">{dayjs(selected.periodeFin).format('DD/MM/YYYY')}</Descriptions.Item>
            <Descriptions.Item label="Fréquence">{selected.frequence}</Descriptions.Item>
            <Descriptions.Item label="Statut">
              <Tag color={STATUS_CONFIG[selected.status]?.color}>{STATUS_CONFIG[selected.status]?.label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Nb commandes">{selected.nombreCommandes}</Descriptions.Item>
            <Descriptions.Item label="Méthode paiement">{selected.methodePaiement}</Descriptions.Item>
            <Descriptions.Item label="Montant brut">{selected.montantBrut} F</Descriptions.Item>
            <Descriptions.Item label="Commission TIGOON">{selected.commissionTotale} F</Descriptions.Item>
            <Descriptions.Item label="Frais">{selected.frais || 0} F</Descriptions.Item>
            <Descriptions.Item label="Net à payer">
              <strong style={{ color: '#52c41a' }}>{selected.montantNet} F</strong>
            </Descriptions.Item>
            {selected.referenceExterne && (
              <Descriptions.Item label="Référence" span={2}>{selected.referenceExterne}</Descriptions.Item>
            )}
            {selected.notes && (
              <Descriptions.Item label="Notes" span={2}>{selected.notes}</Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default Reversements;
